import { useEffect, useRef, useState, useCallback, ReactNode } from 'react'
import { Icon } from 'react-feather'
import { FiCheckCircle, FiXCircle } from 'react-icons/fi'
import NumberFormat from 'react-number-format'
import { Spinner } from 'reactstrap'

import { useField } from '@unform/core'

import { formatMoneyInputs, formatMoneyInputsWithoutSymbol } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'

import { Container, IconWrapper, MessageError, MessageHelp } from './styles'

/**
 *
 * @example
 * <InputMoney
 *   name="main_phone"
 *   placeholder="Insira o telefone"
 *   loading={loading}
 * />
 *
 * @param {string} name Recebe o name para o input
 * @param {ReactElement | string} icon Ícone que será exbido a esquerda do input.
 * @param {boolean} loading Exibe um loader ao lado do input
 * @param {function} onBlur Extendendo onBlur
 *
 * @returns O valor retornado é um objeto com value(formatado) e numAsString(sem formatação)
 *
 */

interface IputMoneyProps {
  id?: string
  name: string
  icon?: Icon
  htmlIcon?: ReactNode
  htmlRightIcon?: ReactNode
  onBlur?: (value?: {
    value: string
    name: string
    clearError: () => void
    setIsFilled: (value: boolean) => void
  }) => void
  iconRight?: ReactNode
  disabled?: boolean
  showWarningOnRequired?: boolean
  showBadgeIsValid?: boolean
  showBadgeIsInvalid?: boolean
  showBorderInvalid?: boolean
  onChange?: (value?: InputMoneyValuesOnChange) => void
  helpText?: string
  placeholder?: string
  required?: boolean
  loading?: boolean
  hideSymbol?: boolean
}

const InputMoney = ({
  name,
  icon: Icon,
  htmlIcon,
  htmlRightIcon,
  onBlur,
  loading,
  iconRight,
  disabled = false,
  showWarningOnRequired = true,
  showBadgeIsValid = false,
  showBadgeIsInvalid = false,
  showBorderInvalid = false,
  onChange,
  helpText,
  hideSymbol = false,
  ...rest
}: IputMoneyProps) => {
  const { lengthCentsCurrency, currencyUseCents } = useAppInfo()

  const inputRef = useRef(null)

  const { fieldName, defaultValue, error, registerField, clearError } =
    useField(name)

  const [isFilled, setIsFilled] = useState(
    defaultValue !== null && defaultValue !== undefined && defaultValue !== ''
  )
  const [valueInput, setValueInput] = useState(() => {
    if (defaultValue === null || defaultValue === undefined) return ''
    if (typeof defaultValue === 'number') {
      return currencyUseCents
        ? defaultValue.toFixed(lengthCentsCurrency)
        : String(defaultValue)
    }
    return String(defaultValue)
  })

  const handleInputBlur = useCallback(() => {
    onBlur?.({
      value: inputRef.current.state.value,
      name: inputRef.current.state.name,
      clearError,
      setIsFilled
    })

    setIsFilled(!!inputRef.current?.state.value)
  }, [clearError, onBlur])

  const defaultValueIsNull = defaultValue === null

  const defaultValueIsUndefined = typeof defaultValue === 'undefined'

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      getValue: ref => ref.state,
      setValue: (_, value) => {
        const newValue = String(value)
        setValueInput(newValue)
      },
      clearValue: () => {
        setValueInput('')
      }
    })
  }, [inputRef, fieldName, registerField, valueInput])

  useEffect(() => {
    if (!defaultValueIsNull && !defaultValueIsUndefined) {
      if (typeof defaultValue === 'number') {
        const data = currencyUseCents
          ? defaultValue.toFixed(lengthCentsCurrency)
          : defaultValue
        setValueInput(String(data))
      } else {
        setValueInput(defaultValue)
      }
    }
  }, [
    defaultValue,
    defaultValueIsNull,
    defaultValueIsUndefined,
    currencyUseCents,
    lengthCentsCurrency
  ])

  const inputChangedHandler = (values: InputMoneyValuesOnChange) => {
    setValueInput(values.value)
    onChange?.(values)
  }

  useEffect(() => {
    if (defaultValue) {
      setIsFilled(true)
    }
  }, [defaultValue])

  return (
    <>
      <Container
        isErrored={!!error || showBadgeIsInvalid || showBorderInvalid}
        isDisabled={disabled}
        showWarning={!isFilled && showWarningOnRequired && rest?.required}
      >
        {Icon && <Icon />}

        {htmlIcon && <div className="htmlIcon">{htmlIcon}</div>}

        <NumberFormat
          type="text"
          onBlur={handleInputBlur}
          name={fieldName}
          aria-label={fieldName}
          format={
            hideSymbol ? formatMoneyInputsWithoutSymbol : formatMoneyInputs
          }
          onValueChange={inputChangedHandler}
          value={valueInput}
          defaultValue={defaultValue === null ? '' : defaultValue}
          disabled={disabled}
          {...rest}
          ref={inputRef}
        />

        {showBadgeIsValid && !!valueInput && (
          <IconWrapper className="success rightElement">
            <FiCheckCircle />
          </IconWrapper>
        )}

        {showBadgeIsInvalid && !!valueInput && (
          <IconWrapper className="error rightElement">
            <FiXCircle />
          </IconWrapper>
        )}

        {loading && (
          <Spinner
            className="rightElement"
            type="grow"
            size="sm"
            color="secondary"
          />
        )}

        {iconRight && (
          <div className="right-icon rightElement">{iconRight}</div>
        )}

        {htmlRightIcon && (
          <div className="htmlIcon htmlRightIcon">{htmlRightIcon}</div>
        )}
      </Container>
      {helpText && <MessageHelp className="help-text">{helpText}</MessageHelp>}
      {error && <MessageError>{error}</MessageError>}
    </>
  )
}

export default InputMoney
